import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/authentication/auth';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { browserLocalPersistence, browserSessionPersistence, setPersistence } from '@angular/fire/auth';
import { LoaderService } from '../../core/ui/loader';
import { AlertService } from '../../core/ui/alert';
import { environment } from '../../../environments/environment';

function passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
  	const value = control.value;

	if(!value) {
		return null;
	}

  	const errors: { [key: string]: any } = {};

  	const minLength = 8;

	if(value.length < minLength) {
		errors['minLength'] = { requiredLength: minLength, actualLength: value.length };
	}

	if(!/[a-z]/.test(value)) {
		errors['hasLowercase'] = true;
	}

	if(!/[A-Z]/.test(value)) {
		errors['hasUppercase'] = true;
	}

	if(!/[0-9]/.test(value)) {
		errors['hasNumeric'] = true;
	}

	if(!/[\^$*.[\]{}()?\"!@#%&/\\,><':;|_~`]/.test(value)) {
		errors['hasSpecial'] = true;
	}

  	return Object.keys(errors).length > 0 ? errors : null;
}

@Component({
	selector: 'app-login',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatCheckboxModule, MatIconModule],
	templateUrl: './login.html',
	styleUrl: './login.css'
})
export class Login implements OnInit {
	loginForm!: FormGroup;
	firebaseError: string | null = null;
	readonly isDev = !environment.production;

	hidePassword = true;

	constructor(private formBuilder: FormBuilder, private authService: AuthService, private router: Router, private loaderService: LoaderService, private alertService: AlertService) {}

	ngOnInit(): void {
		this.loginForm = this.formBuilder.group({
			email: ['', [Validators.required, Validators.email]],
			password: ['', [Validators.required, passwordComplexityValidator]],
			rememberMe: [false]
		});
	}

	get emailControl() {
		return this.loginForm.get('email');
	}

	get passwordControl() {
		return this.loginForm.get('password');
	}

	togglePasswordVisibility(): void {
		this.hidePassword = !this.hidePassword;
	}

	async onSubmit(): Promise<void> {
		this.loginForm.markAllAsTouched();
		this.firebaseError = null;

		if(this.loginForm.valid) {
			this.loaderService.show();

			const { email, password, rememberMe } = this.loginForm.value;

			try {
				const authInstance = this.authService.authInstance;

				if(rememberMe) {
					await setPersistence(authInstance, browserLocalPersistence);
				} else {
					await setPersistence(authInstance, browserSessionPersistence);
				}

				await this.authService.login(email, password);

				this.loaderService.hide();
				this.router.navigate(['/dashboard']);
			} catch (error: any) {
				switch (error.code) {
					case 'auth/invalid-email':
					case 'auth/user-not-found':
					case 'auth/wrong-password':
						this.firebaseError = 'Correo electrónico o contraseña incorrectos.';
					break;
					case 'auth/user-disabled':
						this.firebaseError = 'Tu cuenta ha sido deshabilitada. Contacta al administrador.';
					break;
					case 'auth/too-many-requests':
                        this.firebaseError = 'Has realizado demasiados intentos fallidos. Por favor, inténtalo de nuevo más tarde.';
                    break;
                    case 'auth/network-request-failed':
                        this.firebaseError = 'Problemas de conexión. Por favor, verifica tu conexión a internet.';
                    break;
                    case 'auth/invalid-credential':
                        this.firebaseError = 'Las credenciales proporcionadas no son válidas.';
                    break;
                    case 'auth/operation-not-allowed':
                        this.firebaseError = 'El inicio de sesión con email/contraseña no está habilitado. Contacta al administrador.';
                    break;
                    case 'auth/internal-error':
                    case 'auth/unauthorized-domain':
                        this.firebaseError = 'Ha ocurrido un error interno. Por favor, inténtalo de nuevo.';
                    break;
					default:
						this.firebaseError = 'Ocurrió un error inesperado al iniciar sesión. Inténtalo de nuevo.';
					break;
				}

				this.alertService.error(this.firebaseError, 'Error');
				this.loaderService.hide();
			}
		}
	}
}
